import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import classNames from 'classnames';

import {
    Sizes,
    Typography,
    AnchorAnimation,
    AccordionBasicItem,
    ChecklistBlock,
    HorizontalGaugeBlock,
    UserReviews,
    Paragraph,
    renderMessage,
    DefaultIntlFormatValues,
    PageBehaviourButton,
    navigateWindowTo,
} from '@ratehub/base-ui';
import {
    COMMON_MESSAGES,
    CreditCardShape,
    getCreditCardSchema,
    getLocalizedTravelFeatures,
    trackHeapCCWebClickEvent,
} from '@ratehub/cc-common';
import { RichTextBlock } from '@ratehub/web-components';

import RightSideBigAdContainer from './RightSideBigAdContainer';


function CardDetailsAccordion({ cardInfo, isUserReviewsExpanded, onUserReviewsToggle, className }) {
    const intl = useIntl();

    // How to redeem
    const hasRedemptionLevel = !!cardInfo?.redemptionFlexibility.level;
    const hasRedemptionDescription = !!cardInfo?.howToRedeemDescription;

    // Benefits & features
    const localizedFeatures = getLocalizedTravelFeatures(cardInfo.features);

    const ratehubReviewRating = cardInfo?.ratehubReview?.rating;


    return (
        <RightSideBigAdContainer
            className={className}
            hasAd={true}
        >
            <h2 className={classNames('rh-title-m', 'showDot')}>
                <FormattedMessage
                    id="cc.details.detailed-card-summary-title"
                    defaultMessage="Detailed card summary"
                />
            </h2>

            {/* Benefits and features -- each of which is optional */}
            <If condition={localizedFeatures.length || cardInfo.bonusDescriptionMarkup}>
                <AccordionBasicItem
                    title={(
                        <FormattedMessage
                            id="cc.details.benefits-title"
                            defaultMessage="Benefits & features"
                        />
                    )}
                    data-name="credit-card-details.benefits-and-features"
                    iconKey="diamond"
                    alwaysRenderChildMarkup={true}
                    isInitiallyExpanded={true}
                >
                    <MyAccordionContentContainer>
                        <ChecklistBlock
                            features={localizedFeatures}
                            className="rh-mb-1"
                        />

                        <If condition={cardInfo.bonusDescriptionMarkup}>
                            <MySimpleTypographyWrapper
                                {...renderMessage(cardInfo.bonusDescriptionMarkup, intl)}
                            />
                        </If>
                    </MyAccordionContentContainer>
                </AccordionBasicItem>
            </If>


            {/* How to redeem -- one of two possible formats */}
            <If condition={hasRedemptionLevel || hasRedemptionDescription}>
                <AccordionBasicItem
                    title={(
                        <FormattedMessage
                            id="cc.details.how-to-redeem-title"
                            defaultMessage="How to redeem"
                        />
                    )}
                    data-name="credit-card-details.how-to-redeem"
                    iconKey="piggy"
                    alwaysRenderChildMarkup={true}
                >
                    <MyAccordionContentContainer>
                        <Choose>
                            <When condition={hasRedemptionLevel}>
                                <HorizontalGaugeBlock
                                    title={intl.formatMessage(COMMON_MESSAGES.redemptionFlexibilityTitle)}
                                    level={cardInfo.redemptionFlexibility.level}
                                    caption={cardInfo.redemptionFlexibility.description}
                                />
                            </When>
                            <When condition={hasRedemptionDescription}>
                                <Paragraph
                                    message={cardInfo.howToRedeemDescription}
                                />
                            </When>
                        </Choose>
                    </MyAccordionContentContainer>
                </AccordionBasicItem>
            </If>


            {/* How to get approved -- always relevant, some bullet points conditional */}
            <AccordionBasicItem
                title={(
                    <FormattedMessage
                        id="cc.details.how-to-get-approved-title"
                        defaultMessage="How to get approved"
                    />
                )}
                data-name="credit-card-details.how-to-get-approved"
                iconKey="checkmark"
                alwaysRenderChildMarkup={true}
            >
                <MyAccordionContentContainer>
                    <Paragraph
                        size="medium"
                        weight="medium"
                        as="h3"
                    >
                        <FormattedMessage
                            id="cc.details.eligibility-title"
                            defaultMessage="Eligibility"
                        />
                    </Paragraph>

                    <MySimpleTypographyWrapper>
                        <ul
                            {...renderMessage({
                                ...ELIGIBILITY_BULLETS.eligibility,
                                values: {
                                    ...cardInfo,
                                    minimumIncomeThousands: cardInfo.minimumIncome / 1000,
                                    minimumHouseholdIncomeThousands: cardInfo.minimumHouseholdIncome / 1000,
                                    isMonetizedOrHidden: cardInfo.isMonetized || cardInfo.isHidden,
                                    learnMoreElsewhere: msg => (
                                        <PageBehaviourButton
                                            data-name="cta-modal-learn-more"
                                            size="extra-small"
                                            shouldLowerCaseMessage={false}
                                            onClick={() => {
                                                trackHeapCCWebClickEvent(cardInfo);
                                                navigateWindowTo(cardInfo.applyRedirectURL, { isNewTab: true });
                                            }}
                                            message={msg}
                                        />
                                    ),
                                },
                            }, intl)}
                        />
                    </MySimpleTypographyWrapper>
                </MyAccordionContentContainer>
            </AccordionBasicItem>

            {/* User reviews */}
            <If condition={cardInfo.reviewCount}>
                <AccordionBasicItem
                    isExpanded={isUserReviewsExpanded}
                    onClick={onUserReviewsToggle}
                    title={(
                        <FormattedMessage
                            id="cc.details.user-reviews-title"
                            defaultMessage="User reviews"
                        />
                    )}
                    data-name="credit-card-details.user-reviews"
                    iconKey="people"
                    id={USER_REVIEWS_DOM_ID}
                    alwaysRenderChildMarkup={true}
                >
                    <MyAccordionContentContainer>
                        <UserReviews
                            title={(
                                <FormattedMessage
                                    id="cc.details.user-reviews-card-title"
                                    defaultMessage="Average user score for {name}"
                                    values={{ name: cardInfo.name }}
                                />
                            )}
                            overallRatingPercent={cardInfo.reviewRating}
                            reviews={cardInfo.reviews}
                        />
                    </MyAccordionContentContainer>
                </AccordionBasicItem>
            </If>

            {/* Ratehub review */}
            <If condition={cardInfo.ratehubReview?.content}>
                <script
                    type="application/ld+json"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(getCreditCardSchema(cardInfo), null, 4),
                    }}
                />

                <AccordionBasicItem
                    title={(
                        <FormattedMessage
                            id="cc.details.ratehub-review-title"
                            defaultMessage="Ratehub review"
                        />
                    )}
                    data-name="credit-card-details.ratehub-review"
                    iconKey="credit-card"
                    alwaysRenderChildMarkup={true}
                >
                    <MyAccordionContentContainer>
                        <RichTextBlock
                            content={cardInfo.ratehubReview.content}
                            showAuthor={true}
                            dateDisplayType={'modified'}
                            starRating={ratehubReviewRating > 0
                                ? ratehubReviewRating * 20  // rated as 0 — 5 in the CMS; convert to %
                                : undefined}
                            // eslint-disable-next-line jsx-control-statements/jsx-use-if-tag
                            starMessage={ratehubReviewRating > 0
                                ? (
                                    <FormattedMessage
                                        id="cc.details.ratehub-review-rating"
                                        defaultMessage="<strong>{rating, number} &nbsp;|&nbsp; Our rating</strong>"
                                        values={{
                                            rating: ratehubReviewRating,
                                            ...DefaultIntlFormatValues,
                                        }}
                                    />
                                )
                                : undefined
                            }
                            data-test-name="ratehub-review"
                        />
                    </MyAccordionContentContainer>
                </AccordionBasicItem>
            </If>

            {/* Legal -- optional, but expected */}
            <If condition={cardInfo.legalMarkup}>
                <AccordionBasicItem
                    title={(
                        <FormattedMessage
                            id="cc.details.legal-title"
                            defaultMessage="Legal"
                        />
                    )}
                    data-name="credit-card-details.legal"
                    alwaysRenderChildMarkup={true}
                    iconKey="paper"
                >
                    <MyAccordionContentContainer>
                        <MySimpleTypographyWrapper
                            {...renderMessage(cardInfo.legalMarkup, intl)}
                        />
                    </MyAccordionContentContainer>
                </AccordionBasicItem>
            </If>
        </RightSideBigAdContainer>
    );
}

CardDetailsAccordion.propTypes = {
    cardInfo: CreditCardShape.isRequired,
    isUserReviewsExpanded: PropTypes.bool.isRequired,
    onUserReviewsToggle: PropTypes.func.isRequired,
    className: PropTypes.string,
};

CardDetailsAccordion.defaultProps = {
    className: undefined,
};


const MyAccordionContentContainer = styled.div`
    > ul {
        > li {
            padding-left: ${Sizes.SPACING.HALF};
            margin-left: -${Sizes.SPACING.THREE_QUARTERS};
            margin-bottom: ${Sizes.SPACING.THREE_QUARTERS};

            b {
                font-weight: ${Typography.WEIGHTS.MEDIUM};
            }
        }
    }
`;

// this is styling markup edited within the CMS
const MySimpleTypographyWrapper = styled.div`
    a {
        ${AnchorAnimation}
    }

    ul {
        font-size: ${Sizes.FONTS.XS};

        margin-top: 0;
        margin-bottom: 0;
        padding-left: ${Sizes.SPACING.ONE};

        li {
            margin-bottom: ${Sizes.SPACING.THREE_QUARTERS};
            padding-left: ${Sizes.SPACING.HALF};
            line-height: 1.3;

            &:last-child {
                margin-bottom: 0;
            }
        }
    }
`;

const ELIGIBILITY_BULLETS = defineMessages({
    eligibility: {
        id: 'cc.details.eligibility-bullets',
        defaultMessage: `<li>Minimum income <b>{minimumIncomeThousands, select,
    0 {Any}
    other {{minimumIncomeThousands, number, CAD0}K/yr}
}</b></li>
<li>Household income <b>{minimumHouseholdIncomeThousands, select,
    0 {Any}
    other {{minimumHouseholdIncomeThousands, number, CAD0}K/yr}
}</b></li>
<li>Recommended credit score <b>{minimumCreditScore}+</b></li>
<li>You are a permanent Canadian resident and have reached the age of majority in the province in which you live.</li>
{isMonetizedOrHidden, select,
    true {}
    other {<li><learnMoreElsewhere>learn more on provider website</learnMoreElsewhere></li>}
}
`,
    },
});

const USER_REVIEWS_DOM_ID = 'user-reviews';   /* useful for targeting of scrolling and toggling */

export { USER_REVIEWS_DOM_ID };
export default CardDetailsAccordion;
